import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Monitor, 
  AlertTriangle, 
  DollarSign, 
  TrendingUp, 
  Activity,
  UserPlus,
  AlertCircle,
  BarChart3,
  Calendar
} from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        setError('Failed to fetch dashboard data');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue", trend = null }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-50`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center">
          <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
          <span className="text-sm text-green-600">{trend}</span>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Admin Dashboard</h1>
          <p className="text-gray-600">Overview of URL Monitor Pro system status</p>
        </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Users}
          title="Total Users"
          value={stats?.total_users || 0}
          subtitle={`${stats?.active_users || 0} active`}
          color="blue"
        />
        
        <StatCard
          icon={Monitor}
          title="Total Monitors"
          value={stats?.total_monitors || 0}
          subtitle={`${stats?.active_monitors || 0} active`}
          color="green"
        />
        
        <StatCard
          icon={AlertTriangle}
          title="Total Incidents"
          value={stats?.total_incidents || 0}
          subtitle={`${stats?.open_incidents || 0} open`}
          color="red"
        />
        
        <StatCard
          icon={DollarSign}
          title="Est. Monthly Revenue"
          value={`$${stats?.revenue_stats?.estimated_monthly || 0}`}
          subtitle="Based on subscriptions"
          color="purple"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={UserPlus}
          title="Recent Signups"
          value={stats?.recent_signups || 0}
          subtitle="Last 7 days"
          color="indigo"
        />
        
        <StatCard
          icon={AlertCircle}
          title="Recent Incidents"
          value={stats?.recent_incidents || 0}
          subtitle="Last 24 hours"
          color="orange"
        />
        
        <StatCard
          icon={Activity}
          title="Pro Users"
          value={stats?.subscription_stats?.pro || 0}
          subtitle={`$${stats?.revenue_stats?.pro_revenue || 0}/month`}
          color="blue"
        />
        
        <StatCard
          icon={BarChart3}
          title="Business Users"
          value={stats?.subscription_stats?.business || 0}
          subtitle={`$${stats?.revenue_stats?.business_revenue || 0}/month`}
          color="green"
        />
      </div>

      {/* Enterprise Tier Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <StatCard
          icon={Activity}
          title="Enterprise Users"
          value={stats?.subscription_stats?.enterprise || 0}
          subtitle={`$${stats?.revenue_stats?.enterprise_revenue || 0}/month`}
          color="purple"
        />
        
        <StatCard
          icon={TrendingUp}
          title="Average Revenue Per User"
          value={stats?.total_users > 0 ? `$${Math.round((stats?.revenue_stats?.estimated_monthly || 0) / stats.total_users)}` : '$0'}
          subtitle="Monthly ARPU"
          color="indigo"
        />
      </div>

      {/* Subscription Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Subscription Distribution</h3>
          <div className="space-y-4">
            {stats?.subscription_stats && Object.entries(stats.subscription_stats).map(([tier, count]) => (
              <div key={tier} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full mr-3 ${
                    tier === 'free' ? 'bg-gray-400' :
                    tier === 'pro' ? 'bg-blue-500' : 
                    tier === 'business' ? 'bg-green-500' :
                    tier === 'enterprise' ? 'bg-purple-500' : 'bg-gray-400'
                  }`}></div>
                  <span className="text-sm font-medium text-gray-700 capitalize">{tier}</span>
                </div>
                <div className="text-right">
                  <span className="text-lg font-semibold text-gray-900">{count}</span>
                  <span className="text-sm text-gray-500 block">
                    {stats.total_users ? Math.round((count / stats.total_users) * 100) : 0}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">System Health</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Active Monitors</span>
              <span className="text-sm font-medium text-green-600">
                {stats?.active_monitors} / {stats?.total_monitors}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Open Incidents</span>
              <span className={`text-sm font-medium ${
                (stats?.open_incidents || 0) > 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {stats?.open_incidents || 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Active Users</span>
              <span className="text-sm font-medium text-blue-600">
                {stats?.active_users} / {stats?.total_users}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">System Status</span>
              <span className="text-sm font-medium text-green-600 flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                Operational
              </span>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default AdminDashboard;